/**
 * Centralized Error Service
 * Handles error logging, reporting, and user notifications
 */

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Error categories
export enum ErrorCategory {
  GRAPHQL = 'graphql',
  API = 'api',
  AUTH = 'auth',
  NETWORK = 'network',
  RENDER = 'render',
  VALIDATION = 'validation',
  UNKNOWN = 'unknown',
}

interface ErrorOptions {
  category?: ErrorCategory;
  severity?: ErrorSeverity;
  silent?: boolean;
  extra?: Record<string, unknown>;
  onNotify?: (message: string, severity: ErrorSeverity) => void;
}

interface ErrorMetadata {
  category: ErrorCategory;
  severity: ErrorSeverity;
  extra: Record<string, unknown>;
  timestamp: string;
}

interface ErrorResult {
  error: Error;
  category: ErrorCategory;
  severity: ErrorSeverity;
  userMessage: string;
  metadata: ErrorMetadata;
}

interface GraphQLError {
  message: string;
  [key: string]: unknown;
}

interface ExtendedError extends Error {
  graphQLErrors?: GraphQLError[];
  networkError?: Error;
  statusCode?: number;
  response?: { status: number };
  userMessage?: string;
}

// SentryScope and SentryInstance interfaces are defined in src/types/global.d.tsx

/**
 * Error context for additional metadata
 */
class ErrorContext {
  private context: Record<string, unknown>;

  constructor() {
    this.context = {};
  }

  set(key: string, value: unknown): this {
    this.context[key] = value;
    return this;
  }

  get(key: string): unknown {
    return this.context[key];
  }

  getAll(): Record<string, unknown> {
    return { ...this.context };
  }

  clear(): void {
    this.context = {};
  }
}

const errorContext = new ErrorContext();

/**
 * Global notifier — registered at app startup to show toast notifications
 */
let globalNotify: ((message: string, severity: ErrorSeverity) => void) | null =
  null;

export const registerGlobalNotify = (
  notifier: (message: string, severity: ErrorSeverity) => void,
): void => {
  globalNotify = notifier;
};

const isNetworkError = (error: ExtendedError): boolean =>
  error.name === 'TypeError' &&
  Boolean(
    error.message?.includes('fetch') || error.message?.includes('network'),
  );

const isAuthError = (error: ExtendedError): boolean => {
  const msg = error.message?.toLowerCase() ?? '';
  if (msg.includes('unauthorized') || msg.includes('authentication'))
    return true;
  return error.statusCode === 401 || error.statusCode === 403;
};

const isValidationError = (error: ExtendedError): boolean =>
  error.name === 'ValidationError' ||
  Boolean(error.message?.toLowerCase().includes('validation'));

/**
 * Determine error category based on error object
 */
const categorizeError = (
  error: ExtendedError | null | undefined,
): ErrorCategory => {
  if (!error) return ErrorCategory.UNKNOWN;
  if (isNetworkError(error)) return ErrorCategory.NETWORK;
  if (error.graphQLErrors?.length || error.networkError)
    return ErrorCategory.GRAPHQL;
  if (isAuthError(error)) return ErrorCategory.AUTH;
  if (error.statusCode || error.response) return ErrorCategory.API;
  if (isValidationError(error)) return ErrorCategory.VALIDATION;
  return ErrorCategory.UNKNOWN;
};

/**
 * Determine error severity based on category and error details
 */
const determineSeverity = (
  error: ExtendedError,
  category: ErrorCategory,
): ErrorSeverity => {
  if (category === ErrorCategory.AUTH) return ErrorSeverity.HIGH;
  if (category === ErrorCategory.NETWORK) return ErrorSeverity.MEDIUM;
  if (category === ErrorCategory.RENDER) return ErrorSeverity.CRITICAL;

  // Check status codes
  const statusCode = error.statusCode || error.response?.status;
  if (statusCode && statusCode >= 500) return ErrorSeverity.HIGH;
  if (statusCode && statusCode >= 400) return ErrorSeverity.MEDIUM;

  return ErrorSeverity.LOW;
};

/**
 * Send error to Sentry
 */
const sendToSentry = (
  error: Error,
  metadata: Partial<ErrorMetadata> = {},
): void => {
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.withScope((scope: SentryScope) => {
      // Set tags
      if (metadata.category) scope.setTag('category', metadata.category);
      if (metadata.severity) scope.setTag('severity', metadata.severity);

      // Set extra context
      scope.setExtras({
        ...errorContext.getAll(),
        ...metadata.extra,
      });

      // Set user context if available
      const userId = errorContext.get('userId');
      if (userId) {
        scope.setUser({ id: userId as string });
      }

      window.Sentry!.captureException(error);
    });
  }
};

/**
 * Log error to console in development
 */
const logToDev = (
  error: Error,
  metadata: Partial<ErrorMetadata> = {},
): void => {
  if (process.env.NODE_ENV === 'development') {
    /* eslint-disable no-console -- Development logging */
    console.group(`[Error Service] ${metadata.category || 'Unknown'}`);
    console.error('Error:', error);
    console.log('Severity:', metadata.severity);
    console.log('Context:', errorContext.getAll());
    if (metadata.extra) console.log('Extra:', metadata.extra);
    console.groupEnd();
    /* eslint-enable no-console */
  }
};

/**
 * Get user-friendly error message
 */
const getUserMessage = (
  error: ExtendedError,
  category: ErrorCategory,
): string => {
  // Default messages by category
  const defaultMessages: Record<ErrorCategory, string> = {
    [ErrorCategory.NETWORK]:
      'Unable to connect to the server. Please check your internet connection.',
    [ErrorCategory.AUTH]: 'Your session has expired. Please log in again.',
    [ErrorCategory.API]: 'Something went wrong. Please try again later.',
    [ErrorCategory.GRAPHQL]: 'Failed to load data. Please refresh the page.',
    [ErrorCategory.VALIDATION]: 'Please check your input and try again.',
    [ErrorCategory.RENDER]: 'An error occurred while displaying this page.',
    [ErrorCategory.UNKNOWN]: 'An unexpected error occurred. Please try again.',
  };

  // Check for custom message in error
  if (error.userMessage) return error.userMessage;

  // GraphQL errors often have useful messages
  if (error.graphQLErrors?.[0]?.message) {
    const gqlMessage = error.graphQLErrors[0].message;
    // Don't expose internal error messages
    if (!gqlMessage.includes('Internal') && gqlMessage.length < 100) {
      return gqlMessage;
    }
  }

  return defaultMessages[category] || defaultMessages[ErrorCategory.UNKNOWN];
};

/**
 * Main error handling function
 *
 * @param error - The error object
 * @param options - Options for error handling
 * @returns Error details including user message
 */
export const handleError = (
  error: Error,
  options: ErrorOptions = {},
): ErrorResult => {
  const {
    category: categoryOverride,
    severity: severityOverride,
    extra = {},
    silent = false,
    onNotify,
  } = options;

  // Categorize and determine severity
  const category = categoryOverride || categorizeError(error as ExtendedError);
  const severity =
    severityOverride || determineSeverity(error as ExtendedError, category);

  const metadata: ErrorMetadata = {
    category,
    severity,
    extra,
    timestamp: new Date().toISOString(),
  };

  // Log in development
  logToDev(error, metadata);

  // Send to Sentry in production
  if (process.env.NODE_ENV === 'production' && severity !== ErrorSeverity.LOW) {
    sendToSentry(error, metadata);
  }

  // Get user-friendly message
  const userMessage = getUserMessage(error as ExtendedError, category);

  // Notify user if not silent
  if (!silent) {
    const notifier = onNotify || globalNotify;
    if (notifier) {
      notifier(userMessage, severity);
    }
  }

  return {
    error,
    category,
    severity,
    userMessage,
    metadata,
  };
};

/**
 * Set global error context
 *
 * @param key - Context key
 * @param value - Context value
 */
export const setErrorContext = (key: string, value: unknown): void => {
  errorContext.set(key, value);
};

/**
 * Clear all error context
 */
export const clearErrorContext = (): void => {
  errorContext.clear();
};

/**
 * Create a scoped error handler with pre-configured options
 *
 * @param defaultOptions - Default options for the handler
 * @returns Scoped error handler
 */
export const createErrorHandler = (
  defaultOptions: ErrorOptions = {},
): ((error: Error, options?: ErrorOptions) => ErrorResult) => {
  return (error: Error, options: ErrorOptions = {}): ErrorResult => {
    return handleError(error, { ...defaultOptions, ...options });
  };
};

/**
 * Async error wrapper
 * Wraps an async function with error handling
 *
 * @param fn - Async function to wrap
 * @param options - Error handling options
 * @returns Wrapped function
 */
export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  options: ErrorOptions = {},
): ((...args: T) => Promise<R>) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error as Error, options);
      throw error; // Re-throw for caller to handle if needed
    }
  };
};

export default {
  handleError,
  setErrorContext,
  clearErrorContext,
  createErrorHandler,
  withErrorHandling,
  ErrorSeverity,
  ErrorCategory,
};
